import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
    Minus,
    Plus,
    Check,
    ShareNetwork,
    ArrowLeft,
    Copy,
} from "@phosphor-icons/react";
import ArtDecoDivider from "../components/ArtDecoDivider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
    { value: "cocktail_party", label: "Cocktail Party" },
    { value: "wedding", label: "Wedding" },
    { value: "casual_gathering", label: "Casual Gathering" },
    { value: "corporate", label: "Corporate" },
    { value: "birthday", label: "Birthday" },
    { value: "holiday_party", label: "Holiday Party" },
];

const SPIRITS = [
    { value: "whiskey", label: "Whiskey" },
    { value: "gin", label: "Gin" },
    { value: "vodka", label: "Vodka" },
    { value: "tequila", label: "Tequila" },
    { value: "rum", label: "Rum" },
    { value: "red_wine", label: "Red Wine" },
    { value: "white_wine", label: "White Wine" },
    { value: "rose_wine", label: "Rosé" },
    { value: "beer", label: "Beer" },
    { value: "champagne", label: "Champagne" },
];

function Stepper({ value, onChange, min = 1, max = 24, smallStep = 1, bigStep = null, testidPrefix = "stepper" }) {
    const clamp = (v) => Math.max(min, Math.min(max, v));
    return (
        <div className="flex items-center gap-3 flex-wrap">
            {bigStep && (
                <button
                    type="button"
                    onClick={() => onChange(clamp(value - bigStep))}
                    className="px-3 h-11 border border-white/15 text-gold text-xs tracking-widest uppercase hover:bg-gold hover:text-obsidian transition-colors"
                    data-testid={`${testidPrefix}-minus-big`}
                >
                    −{bigStep}
                </button>
            )}
            <button
                type="button"
                onClick={() => onChange(clamp(value - smallStep))}
                className="w-11 h-11 border border-white/15 text-gold hover:bg-gold hover:text-obsidian transition-colors flex items-center justify-center"
                data-testid={`${testidPrefix}-minus`}
            >
                <Minus size={16} weight="thin" />
            </button>
            <input
                type="number"
                min={min}
                max={max}
                value={value}
                onChange={(e) => onChange(clamp(Number(e.target.value) || min))}
                className="input-dark w-24 text-center"
                data-testid={`${testidPrefix}-input`}
            />
            <button
                type="button"
                onClick={() => onChange(clamp(value + smallStep))}
                className="w-11 h-11 border border-white/15 text-gold hover:bg-gold hover:text-obsidian transition-colors flex items-center justify-center"
                data-testid={`${testidPrefix}-plus`}
            >
                <Plus size={16} weight="thin" />
            </button>
            {bigStep && (
                <button
                    type="button"
                    onClick={() => onChange(clamp(value + bigStep))}
                    className="px-3 h-11 border border-white/15 text-gold text-xs tracking-widest uppercase hover:bg-gold hover:text-obsidian transition-colors"
                    data-testid={`${testidPrefix}-plus-big`}
                >
                    +{bigStep}
                </button>
            )}
        </div>
    );
}

function StepHeader({ n, title, helper }) {
    return (
        <div className="mb-6">
            <div className="text-gold text-xs tracking-overline uppercase mb-2 flex items-center gap-3">
                <span className="font-display italic text-2xl text-gold/70">{String(n).padStart(2, "0")}</span>
                <span className="w-8 h-px bg-gold/50" />
                Step {n}
            </div>
            <h2 className="font-display text-white text-3xl md:text-4xl mb-2">{title}</h2>
            {helper && <p className="text-white/55 font-light text-sm md:text-base">{helper}</p>}
        </div>
    );
}

export default function Calculator() {
    const [guests, setGuests] = useState(40);
    const [hours, setHours] = useState(4);
    const [eventType, setEventType] = useState("cocktail_party");
    const [selectedSpirits, setSelectedSpirits] = useState(["whiskey", "vodka", "red_wine"]);
    const [allowShots, setAllowShots] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const [copied, setCopied] = useState(false);

    const toggleSpirit = (v) =>
        setSelectedSpirits((prev) =>
            prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]
        );

    const generate = async () => {
        if (selectedSpirits.length === 0) {
            setError("Please choose at least one beverage.");
            return;
        }
        setError("");
        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/calculator`, {
                guest_count: guests,
                hours,
                event_type: eventType,
                spirits: selectedSpirits,
                allow_shots: allowShots,
            });
            setResult(res.data);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) {
            setError("Could not generate list. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const resultText = () => {
        if (!result) return "";
        const lines = [
            `THE SPIRITED GENTLEMAN — Bar Shopping List`,
            `Event: ${result.event_type_label} · Guests: ${result.guest_count} · Hours: ${result.hours}`,
            `Estimated Drinks: ~${result.total_drinks_estimate}`,
            ``,
        ];
        result.sections.forEach((s) => {
            lines.push(`— ${s.title.toUpperCase()} —`);
            s.items.forEach((it) => {
                lines.push(`• ${it.name}: ${it.quantity}${it.note ? `  (${it.note})` : ""}`);
            });
            lines.push("");
        });
        return lines.join("\n");
    };

    const share = async () => {
        const text = resultText();
        if (navigator.share) {
            try {
                await navigator.share({
                    title: "Bar Shopping List — The Spirited Gentleman",
                    text,
                });
                return;
            } catch (err) {
                // User typically cancels the share sheet — this is expected, not an error.
                // Log at debug level and fall through to clipboard copy.
                console.debug("navigator.share dismissed or unavailable:", err);
            }
        }
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Clipboard write failed:", err);
            setError("Could not copy to clipboard.");
        }
    };

    const shareButtonContent = () => {
        if (copied) {
            return (
                <>
                    <Check size={16} weight="thin" className="mr-2" />
                    Copied
                </>
            );
        }
        if (navigator.share) {
            return (
                <>
                    <ShareNetwork size={16} weight="thin" className="mr-2" />
                    Share List
                </>
            );
        }
        return (
            <>
                <Copy size={16} weight="thin" className="mr-2" />
                Copy List
            </>
        );
    };

    // ============================== RESULT VIEW ==============================
    if (result) {
        return (
            <div className="bg-obsidian min-h-screen" data-testid="calculator-result">
                <section className="pt-12 pb-8 px-6 lg:px-12 text-center">
                    <div className="text-gold text-xs tracking-overline uppercase mb-3">
                        Your Shopping List
                    </div>
                    <h1 className="font-display text-white text-4xl md:text-5xl">
                        Stock The <em className="gold-italic">Bar</em>
                    </h1>
                    <ArtDecoDivider />
                </section>

                <section className="px-6 lg:px-12 max-w-4xl mx-auto">
                    {/* Stats card */}
                    <div className="bg-obsidian-soft border border-gold/30 p-6 md:p-8 grid grid-cols-3 gap-2 md:gap-6 mb-8" data-testid="result-stats">
                        <div className="text-center border-r border-white/10 pr-2 md:pr-6">
                            <div className="text-xs tracking-overline uppercase text-white/40 mb-2">Guests</div>
                            <div className="font-display text-gold text-3xl md:text-5xl" data-testid="stat-guests">{result.guest_count}</div>
                        </div>
                        <div className="text-center border-r border-white/10 pr-2 md:pr-6">
                            <div className="text-xs tracking-overline uppercase text-white/40 mb-2">Hours</div>
                            <div className="font-display text-gold text-3xl md:text-5xl" data-testid="stat-hours">{result.hours}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-xs tracking-overline uppercase text-white/40 mb-2">~Drinks</div>
                            <div className="font-display text-gold text-3xl md:text-5xl" data-testid="stat-drinks">{result.total_drinks_estimate}</div>
                        </div>
                    </div>
                    <div className="text-center mb-12">
                        <span className="inline-block px-4 py-1.5 border border-gold/40 text-gold text-xs tracking-overline uppercase" data-testid="result-event-type">
                            {result.event_type_label}
                        </span>
                    </div>

                    {/* Sections */}
                    <div className="space-y-12">
                        {result.sections.map((section) => (
                            <div key={section.title} data-testid={`section-${section.title.replace(/\W+/g, "-").toLowerCase()}`}>
                                <div className="flex items-center gap-4 mb-6">
                                    <h2 className="font-display text-white text-2xl md:text-3xl">{section.title}</h2>
                                    <div className="flex-1 h-px bg-gradient-to-r from-gold/40 to-transparent" />
                                </div>
                                <ul className="space-y-3">
                                    {section.items.map((it, idx) => (
                                        <li
                                            key={`${section.title}-${idx}`}
                                            className="flex items-start justify-between gap-4 py-3 border-b border-white/5"
                                        >
                                            <div>
                                                <div className="text-white font-sans text-base">{it.name}</div>
                                                {it.note && (
                                                    <div className="text-white/40 text-xs italic font-sans mt-1">
                                                        {it.note}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="text-gold font-display text-base whitespace-nowrap">
                                                {it.quantity}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    {/* Actions */}
                    <div className="mt-16 mb-24 flex flex-col sm:flex-row gap-3 justify-center">
                        <button
                            onClick={() => setResult(null)}
                            className="btn-gold-outline"
                            data-testid="result-back"
                        >
                            <ArrowLeft size={16} weight="thin" className="mr-2" />
                            Recalculate
                        </button>
                        <button onClick={share} className="btn-gold" data-testid="result-share">
                            {shareButtonContent()}
                        </button>
                        <Link to="/book" className="btn-gold-outline" data-testid="result-cta-book">
                            Or, Reserve The Evening
                        </Link>
                    </div>
                </section>
            </div>
        );
    }

    // ============================== WIZARD VIEW ==============================
    return (
        <div className="bg-obsidian min-h-screen" data-testid="page-calculator">
            <section className="pt-16 pb-8 px-6 lg:px-12 text-center">
                <div className="text-gold text-xs tracking-overline uppercase mb-3">
                    Bar Calculator
                </div>
                <h1 className="font-display text-white text-5xl md:text-6xl mb-3">
                    Stock The <em className="gold-italic">Bar</em>
                </h1>
                <ArtDecoDivider />
                <p className="text-white/55 max-w-xl mx-auto font-light">
                    Five questions. One tailored shopping list — built on real bartending math, not guesswork.
                </p>
            </section>

            <div className="max-w-3xl mx-auto px-6 lg:px-12 pb-16 space-y-12">
                {/* STEP 1 */}
                <div className="bg-obsidian-soft border border-white/10 p-6 md:p-10" data-testid="step-1">
                    <StepHeader n={1} title="How many guests?" helper="Use ±1 for fine-tuning or ±5 for larger gatherings." />
                    <Stepper
                        value={guests}
                        onChange={setGuests}
                        min={1}
                        max={2000}
                        smallStep={1}
                        bigStep={5}
                        testidPrefix="guests"
                    />
                </div>

                {/* STEP 2 */}
                <div className="bg-obsidian-soft border border-white/10 p-6 md:p-10" data-testid="step-2">
                    <StepHeader n={2} title="How long is the event?" helper="In hours. Most cocktail parties run 3–5." />
                    <Stepper value={hours} onChange={setHours} min={1} max={24} testidPrefix="hours" />
                </div>

                {/* STEP 3 */}
                <div className="bg-obsidian-soft border border-white/10 p-6 md:p-10" data-testid="step-3">
                    <StepHeader n={3} title="What kind of event?" helper="Different occasions, different pours." />
                    <div className="flex flex-wrap gap-2">
                        {EVENT_TYPES.map((et) => {
                            const active = eventType === et.value;
                            return (
                                <button
                                    key={et.value}
                                    type="button"
                                    onClick={() => setEventType(et.value)}
                                    data-testid={`event-chip-${et.value}`}
                                    className={`px-4 py-2 text-xs tracking-overline uppercase border transition-all duration-200 ${
                                        active
                                            ? "bg-gold text-obsidian border-gold"
                                            : "border-white/15 text-white/70 hover:border-gold hover:text-gold"
                                    }`}
                                >
                                    {et.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* STEP 4 */}
                <div className="bg-obsidian-soft border border-white/10 p-6 md:p-10" data-testid="step-4">
                    <StepHeader n={4} title="What will you be serving?" helper="Select any combination — we'll allocate the math accordingly." />
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {SPIRITS.map((s) => {
                            const active = selectedSpirits.includes(s.value);
                            return (
                                <button
                                    key={s.value}
                                    type="button"
                                    onClick={() => toggleSpirit(s.value)}
                                    data-testid={`spirit-tile-${s.value}`}
                                    className={`relative px-4 py-5 border text-sm uppercase tracking-widest transition-all duration-200 ${
                                        active
                                            ? "border-gold bg-gold/10 text-gold"
                                            : "border-white/10 text-white/60 hover:border-gold/60 hover:text-white"
                                    }`}
                                >
                                    {active && (
                                        <Check
                                            size={14}
                                            weight="bold"
                                            className="absolute top-2 right-2"
                                        />
                                    )}
                                    {s.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* STEP 5 */}
                <div className="bg-obsidian-soft border border-white/10 p-6 md:p-10" data-testid="step-5">
                    <StepHeader n={5} title="Will shots be served?" helper="If yes, we'll add ~3 extra pours per guest to whiskey, vodka & tequila." />
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            type="button"
                            onClick={() => setAllowShots(true)}
                            data-testid="shots-yes"
                            className={`flex-1 px-5 py-4 border text-sm tracking-widest uppercase transition-all duration-200 ${
                                allowShots
                                    ? "bg-gold text-obsidian border-gold"
                                    : "border-white/15 text-white/70 hover:border-gold hover:text-gold"
                            }`}
                        >
                            Yes — shots will be served
                        </button>
                        <button
                            type="button"
                            onClick={() => setAllowShots(false)}
                            data-testid="shots-no"
                            className={`flex-1 px-5 py-4 border text-sm tracking-widest uppercase transition-all duration-200 ${
                                !allowShots
                                    ? "bg-gold text-obsidian border-gold"
                                    : "border-white/15 text-white/70 hover:border-gold hover:text-gold"
                            }`}
                        >
                            No shots
                        </button>
                    </div>
                    {allowShots && (
                        <p className="mt-4 text-white/50 text-xs italic font-sans" data-testid="shots-helper">
                            ~3 extra pours per guest added to whiskey, vodka & tequila.
                        </p>
                    )}
                </div>

                {/* SUBMIT */}
                {error && (
                    <div className="px-4 py-3 border border-red-500/30 bg-red-500/10 text-red-300 text-sm font-sans" data-testid="calculator-error">
                        {error}
                    </div>
                )}
                <button
                    onClick={generate}
                    disabled={submitting}
                    className="btn-gold w-full py-5 disabled:opacity-50"
                    data-testid="generate-shopping-list"
                >
                    {submitting ? "Calculating…" : "Generate Shopping List"}
                </button>
            </div>
        </div>
    );
}
